import { resolvePreferences } from '../contract/resolve';
import type { ResolvedPreferences } from '../contract/types';
import type { PreferenceInput } from '../contract/wire';

/**
 * Preference payloads for tests, for the test harness, and for the developer preference toggle.
 *
 * They are wire payloads rather than hand-written resolved objects on purpose. A literal
 * `ResolvedPreferences` would let a test assert against a shape the resolver can no longer produce,
 * so every fixture goes through the real resolver and stays honest by construction.
 */

export const PRESET_IDS = [
  'enUsCustomary',
  'enCaMetric',
  'frCa',
  'esMx',
  'enUsHostile',
  'frFrMetric',
  'jaJpYmd',
  'deDe',
] as const;

export type PresetId = (typeof PRESET_IDS)[number];

export type PreferencePreset = {
  readonly id: PresetId;
  /** Short enough for a table column header or a picker row. */
  readonly label: string;
  /** What this preset breaks that the others do not. */
  readonly summary: string;
  readonly input: PreferenceInput;
};

/**
 * Preference sets a developer can render the whole app under, each breaking a different assumption.
 *
 * The point of a catalogue rather than one "hostile" payload is that assumptions fail
 * independently. A screen can survive French and still break on year-first dates; it can survive
 * metric and still concatenate a hardcoded `mi`. Every entry below is here because it is the only
 * one that catches something, and each is checked against the default on that axis in
 * `preferences.test.ts` — a preset that resolves to the default is worse than no preset, because it
 * reports a screen as passing that was never exercised.
 *
 * Ordered in two groups. First the four locales the platform actually sells into, so a developer
 * checking their screen against shipping markets reads them together; then the synthetic and
 * overseas sets, which exist for the format hazards they carry rather than for anyone using them.
 *
 * Only `enUsHostile` carries an `effectiveUserPreferences` payload. `esMx` could — Mexico is the
 * one other row in `COUNTRIES_WITH_BACKEND_DEFAULTS` — but it describes a real user, and a real
 * Mexican user's codes come from the backend's own Mexican defaults. Everywhere else the country
 * sits outside that list, so the resolver refuses the payload and answers from the locale, which
 * makes writing one a fixture describing a resolution that cannot happen.
 */
export const PREFERENCE_PRESETS = {
  /** The developer default, and what every other preset is contrasted against. */
  enUsCustomary: {
    id: 'enUsCustomary',
    label: 'en-US customary',
    summary:
      'A Detroit user: US customary units, twelve-hour clock, month-first dates. The baseline.',
    input: {
      userPreferences: { locale: 'en-US', timeZone: 'America/Detroit' },
    },
  },

  /** English Canada: year-first dates on a twelve-hour clock, priced in CAD rather than USD. */
  enCaMetric: {
    id: 'enCaMetric',
    label: 'en-CA metric',
    summary:
      'English, metric, year-first dates with a twelve-hour clock, and CAD rather than USD.',
    input: {
      userPreferences: { locale: 'en-CA', timeZone: 'America/Toronto' },
    },
  },

  /**
   * The same country and the same currency as `enCaMetric`, written the other way round.
   *
   * Quebec puts the dollar sign after the amount — `1 234,50 $` against Ontario's `$1,234.50` — so
   * a template building a price as `$${amount}` renders correctly under one and wrongly under the
   * other. The pair catches what neither does alone, which is why both markets are listed.
   */
  frCa: {
    id: 'frCa',
    label: 'fr-CA',
    summary:
      'French Canada: the dollar sign trails the amount and the clock is 24-hour, on the same CAD as en-CA.',
    input: {
      userPreferences: { locale: 'fr-CA', timeZone: 'America/Toronto' },
    },
  },

  /**
   * The only preset reaching the third currency code, and the only one addressed the Mexican way.
   *
   * `MXN` is one of three currencies the package supports and nothing else resolves to it, so
   * without this entry the peso path is never rendered. `MX_STANDARD` is likewise one of two
   * address layouts with no other preset selecting it.
   */
  esMx: {
    id: 'esMx',
    label: 'es-MX',
    summary:
      'Mexico: pesos, day-first dates, metric units, and the only addresses laid out MX_STANDARD rather than US_STANDARD.',
    input: {
      userPreferences: { locale: 'es-MX', timeZone: 'America/Mexico_City' },
    },
  },

  /**
   * The most valuable of the set, because it is the only one that isolates units from language.
   *
   * `en-US` is inside `COUNTRIES_WITH_BACKEND_DEFAULTS`, so the overrides below are honoured rather
   * than discarded. A developer reads their own language and everything else is wrong, which is
   * where a hardcoded `mi` stops hiding behind unfamiliar text.
   */
  enUsHostile: {
    id: 'enUsHostile',
    label: 'en-US hostile',
    summary:
      'English text with metric units, a 24-hour clock and day-first dates. Separates the units a screen assumes from the language it is written in.',
    input: {
      userPreferences: { locale: 'en-US', timeZone: 'America/Detroit' },
      effectiveUserPreferences: {
        measurements: { unitSystem: 'METRIC' },
        formats: {
          date: { styleCode: 'DMY' },
          time: { styleCode: '24H' },
        },
      },
    },
  },

  /** Everything different at once: the closest thing here to a real overseas user. */
  frFrMetric: {
    id: 'frFrMetric',
    label: 'fr-FR metric',
    summary:
      'Comma decimals, narrow-space grouping, metric, 24-hour, day-first, and translated relative time.',
    input: { userPreferences: { locale: 'fr-FR', timeZone: 'Europe/Paris' } },
  },

  /**
   * Year-first dates, which no `DATETIME` style code can express: `date` resolves to
   * `YMD` while `dateTime` borrows `DMY_24H`. Also the only preset whose zone crosses the date line
   * relative to the others, so a date rendered without a zone shows the wrong day.
   */
  jaJpYmd: {
    id: 'jaJpYmd',
    label: 'ja-JP year-first',
    summary:
      'Year-first dates, a zone far enough east to change the calendar day, and the one combination the DATETIME enum cannot name.',
    input: { userPreferences: { locale: 'ja-JP', timeZone: 'Asia/Tokyo' } },
  },

  /**
   * Resolves to the same preferences as `frFrMetric` and still earns its place, because the
   * separators are what `parse` reads and they are not the same: `1.234,5` is a thousand-odd here
   * and rejected in French, where grouping is a narrow no-break space.
   */
  deDe: {
    id: 'deDe',
    label: 'de-DE',
    summary:
      'Dot grouping with comma decimals — the separator pair most likely to be parsed as the wrong number.',
    input: { userPreferences: { locale: 'de-DE', timeZone: 'Europe/Berlin' } },
  },
} as const satisfies Readonly<Record<PresetId, PreferencePreset>>;

/** A Detroit user: US customary units, twelve-hour clock, month-first dates. */
export const TEST_PREFERENCES: PreferenceInput =
  PREFERENCE_PRESETS.enUsCustomary.input;

/** A Paris user: metric units, twenty-four-hour clock, day-first dates. */
export const OTHER_TEST_PREFERENCES: PreferenceInput =
  PREFERENCE_PRESETS.frFrMetric.input;

/** The resolved form of a payload, for tests asserting against resolved values directly. */
export const resolvedFor = (input: PreferenceInput): ResolvedPreferences =>
  resolvePreferences(input).preferences;

/** The resolved form of a named preset, which is what the docs and the harness read. */
export const resolvedPreset = (id: PresetId): ResolvedPreferences =>
  resolvedFor(PREFERENCE_PRESETS[id].input);
